import { acceptHMRUpdate, defineStore } from 'pinia'
import * as Sentry from '@sentry/vue'
import { addMinutes, format, subMinutes, type Day } from 'date-fns'

import {
  HIDDEN_PERMISSION_IDS,
  type TimeoutOption,
  useGlobalStore,
} from './global'

import { HttpError } from '@/helpers/errors'
import { friendlyEntityTypes } from '@/helpers/friendlyNames'
import { isMultiEntityRole, roleToSessionRole } from '@/helpers/user'

import apiService from '@/services/api.service'
import {
  dateToString,
  dateToUTCString,
  formatDateHelper,
  militaryGMTOffsetFromNumeric,
  stringToUTCDate,
} from '@/services/date.service'
import RootService from '@/services/root/root'
import TokenService from '@/services/user/token'
import UserService from '@/services/user/user'
import UserSettingsService from '@/services/user/user_settings'

import type { FeeType } from '@/types/services/fund_product'
import type { Currency, Timezone } from '@/types/services/global'
import type {
  AssistantClientFlatItem,
  AssistantClientMapping,
  Entity,
  EntityType,
  Root,
  RootAccessData,
  RootUserAccessDataRelation,
  RootUserEntityAccessData,
  ValuationDates,
} from '@/types/services/root'
import type {
  CurrentUser,
  Role,
  SessionRole,
  UpdateProfileData,
  UserSession,
} from '@/types/services/user'
import {
  themeValues,
  type TourFlow,
  type UserSettings,
  type UserSettingsWithRequiredAdditional,
} from '@/types/services/user_settings'

import type { BaseRootUser, RootUserRelation } from '@/types/services/root_user'

export interface EntitiesGroup {
  icon: string
  text: string
  entities: Entity[]
}

export type GroupedEntities = {
  [key in EntityType]?: EntitiesGroup
}

export interface EntityWithDetails {
  entity: Entity
  sections: string[]
  rootUser: BaseRootUser
}

export interface UpdateProfileParams {
  first_name: string
  last_name: string
  email: string
  password: string | null
}

export interface UserState {
  initial_auth_check_complete: boolean
  session_started: boolean
  user: CurrentUser | null
  access_data: RootAccessData[]
  access_data_loading: boolean
  entity: Entity | null
  currentView: SessionRole | null
  currentEntityId: string | null
  role: Role | null
  is_logging_out: boolean
  root_user: BaseRootUser | null
  root: Root | null
  settings: UserSettingsWithRequiredAdditional
  fee_types: FeeType[]
  permissions: number[]
  root_user_relations: RootUserRelation[]
  packages: number[]
  activeAssistantClient: AssistantClientFlatItem | null
}

export const useUserStore = defineStore('user', {
  state(): UserState {
    return {
      initial_auth_check_complete: false,
      session_started: false,
      user: null,
      access_data: [],
      access_data_loading: true,
      entity: null,
      currentView: null,
      currentEntityId: null,
      role: null,
      is_logging_out: false,
      root_user: null,
      root: null,
      settings: {
        id: '',
        user_id: '',
        base_currency_id: '',
        base_crypto_id: '',
        confirmed: false,
        two_factor: 'none',
        session_timeout: null,
        last_seen_new_defi_services: null,
        additional: {
          admins_view: 'table',
          profile_icon_color: '#2985E3',
          hide_small: 10,
          hide_small_bool: false,
          table_pagination: 10,
          date_format: 'dd/MM/yyyy',
          privacy_mode: false,
          theme: 'system',
          tour_flow: null,
          week_starts_on: 1,
          clients_show_deactivated: false,
          admins_show_deactivated: false,
          referrals_show_deactivated: false,
          default_session: null,
          last_read_release_notes_version: null,
          last_ai_suggestions_notification_date: null,
          fund_benchmarks: {},
          portfolio_default_filters: null,
          pnl_attribution_defaults: null,
        },
      },
      fee_types: [],
      permissions: [],
      root_user_relations: [],
      packages: [],
      activeAssistantClient: null,
    }
  },

  actions: {
    async initialize() {
      if (!TokenService.getToken()) {
        this.initial_auth_check_complete = true
        return
      }

      apiService.setAuthHeader()
      apiService.mount401Interceptor()

      try {
        await this.getUserData()

        this.setAutoSession()
      } catch (error) {
        Sentry.captureException(error)
        console.error('Error during user initialization:', error)
      } finally {
        this.initial_auth_check_complete = true
      }
    },

    /**
     * Authenticate with email + password.
     *
     * Returns `{ requires_2fa: true, challenge_id }` when the user has 2FA
     * enabled — in that case the caller (SignIn.vue) must collect a TOTP
     * code and call `verifyLogin2fa` to finish login. Otherwise the token is
     * set and `getUserData` is run here.
     */
    async login(
      email: string,
      password: string,
      captchaToken?: string
    ): Promise<{ requires_2fa: boolean; challenge_id: string | null }> {
      const hashedPassword = UserService.hashPassword(
        password,
        email.toLowerCase()
      )

      const response = await UserService.login(
        email,
        hashedPassword,
        captchaToken
      )

      if (response.requires_2fa) {
        return {
          requires_2fa: true,
          challenge_id: response.challenge_id,
        }
      }

      if (!response.token) {
        throw new Error('Login response missing token')
      }

      await this.getUserData()

      return { requires_2fa: false, challenge_id: null }
    },

    /**
     * Complete a 2FA-gated login by exchanging the challenge + TOTP code
     * for an access token, then loading user data.
     */
    async verifyLogin2fa(challenge_id: string, code: string): Promise<void> {
      await UserService.verifyLogin2fa(challenge_id, code)

      await this.getUserData()
    },

    async getUserData() {
      await Promise.all([
        this.getMe(),
        this.getAccessData(),
        this.getUserSettings(),
      ])

      if (!this.user) {
        throw new Error('User data not loaded')
      }

      if (!this.access_data || this.access_data.length === 0) {
        throw new Error('No access data available for this user')
      }
    },

    changeSession(session: UserSession) {
      if (this.isSameSession(session)) {
        return
      }

      apiService.cancelCurrentRequests()
      const { role, entity_id, client_relation_id } = session

      if (role !== 'assistant') {
        this.activeAssistantClient = null
      }

      if (isMultiEntityRole(role)) {
        this.handleMultiEntitySession(role)
        return
      }

      this.handleSingleEntitySession(role, entity_id!)

      if (role === 'assistant' && client_relation_id) {
        const client = this.assistantClientsWithEntity.find(
          (c) => c.relation_id === client_relation_id
        )
        if (client) {
          this.handleAssistantClientSelection(client)
        } else {
          this.activeAssistantClient = null
        }
      } else if (role === 'assistant') {
        this.activeAssistantClient = null
      }
    },

    isSameSession(session: UserSession): boolean {
      if (this.currentView !== session.role) {
        return false
      }

      if (isMultiEntityRole(session.role)) {
        return true
      }

      if (this.entity?.id !== session.entity_id) {
        return false
      }

      if (session.role === 'assistant') {
        return (
          this.activeAssistantClient?.relation_id === session.client_relation_id
        )
      }

      return true
    },

    handleMultiEntitySession(role: SessionRole) {
      const entities =
        role === 'client' ? this.allClientEntities : this.allReferrerEntities

      if (entities.length === 0) {
        throw new Error(`No ${role} entities available`)
      }

      this.$patch({
        session_started: true,
        root: null,
        root_user: null,
        entity: null,
        role,
        permissions: [],
        packages: [],
        currentView: role,
        fee_types: [],
      })

      this.saveSessionToStorage(role)
    },

    handleSingleEntitySession(sessionRole: SessionRole, entityId: string) {
      if (!entityId) {
        throw new Error(`Entity ID is required for role: ${sessionRole}`)
      }

      const sessionData = this.findSessionData(sessionRole, entityId)

      if (!sessionData) {
        throw new Error(
          `Access data not found. Role: ${sessionRole}, Entity ID: ${entityId}`
        )
      }

      const { rootAccessData, entityAccessData, entityRelation } = sessionData

      const role =
        !entityRelation && rootAccessData.root_user.is_superadmin
          ? 'superadmin'
          : sessionRole

      let permissions = entityRelation?.permission_ids ?? []
      // Check if superadmin has set specific permissions
      if (!entityRelation && rootAccessData.root_user.is_superadmin) {
        const adminRelation = entityAccessData.relations.find(
          (r) => r.role === 'admin'
        )
        permissions = adminRelation?.permission_ids ?? []
      }

      this.$patch({
        session_started: true,
        root: rootAccessData.root,
        root_user: rootAccessData.root_user,
        entity: entityAccessData.entity,
        role,
        permissions,
        packages: rootAccessData.root_packages,
        currentView: sessionRole,
        fee_types: entityAccessData.fee_types,
      })

      apiService.setRootUserHeader(rootAccessData.root_user.id)
      apiService.setEntityHeader(entityId)

      this.saveSessionToStorage(sessionRole, entityId)
    },

    saveSessionToStorage(role: SessionRole, entityId?: string) {
      UserService.setCurrentSession(role, entityId)
      UserService.setLastSession(role, entityId)
    },

    findSessionData(
      sessionRole: SessionRole,
      entityId: string
    ): {
      rootAccessData: RootAccessData
      entityAccessData: RootUserEntityAccessData
      entityRelation: RootUserAccessDataRelation | null
    } | null {
      for (const rad of this.access_data) {
        for (const ead of rad.entity_access_list) {
          if (ead.entity.id !== entityId) {
            continue
          }

          // Superadmin accessing as admin
          if (sessionRole === 'admin' && rad.root_user.is_superadmin) {
            return {
              rootAccessData: rad,
              entityAccessData: ead,
              entityRelation: null,
            }
          }

          // Regular role access
          const relation = ead.relations.find((r) => r.role === sessionRole)
          if (relation) {
            return {
              rootAccessData: rad,
              entityAccessData: ead,
              entityRelation: relation,
            }
          }
        }
      }

      return null
    },

    setDefaultSession(): boolean {
      const defaultSession = this.settings?.additional?.default_session

      if (defaultSession) {
        this.changeSession({
          role: roleToSessionRole(defaultSession.role),
          entity_id: defaultSession.entity_id,
        })
        return true
      }
      return false
    },

    setSessionFromUrl(): boolean {
      const entityIdFromUrl = UserService.getFromUrlParamsEntityId()

      if (entityIdFromUrl) {
        const role = this.findRoleByEntityId(entityIdFromUrl)

        if (!role) {
          console.warn(`No role found for entity ID: ${entityIdFromUrl}`)
          return false
        }

        this.changeSession({ role, entity_id: entityIdFromUrl })
        return true
      }
      return false
    },

    setSavedSession(): boolean {
      const session =
        UserService.getCurrentSession() || UserService.getLastSession()

      if (session) {
        this.changeSession(session)
        return true
      }
      return false
    },

    setSingleAccountSession(): boolean {
      if (this.hasMultipleAccounts) {
        return false
      }

      // If user has only client role, start client session without entity
      if (this.hasOnlyClientRole && this.allClientEntities.length > 0) {
        this.changeSession({ role: 'client' })
        return true
      }

      // If user has only referral role, start referral session without entity
      if (this.hasOnlyReferralRole && this.allReferrerEntities.length > 0) {
        this.changeSession({ role: 'referral' })
        return true
      }

      // If user has only assistant role, and he has more than 1 assigned client
      if (
        this.hasOnlyAssistantRole &&
        this.assistantClientsWithEntity.length > 1
      ) {
        return false
      }

      const firstAccess = this.access_data[0]

      if (
        !firstAccess.entity_access_list ||
        firstAccess.entity_access_list.length == 0
      ) {
        console.warn('User has no entities in access list')
        return false
      }

      const firstEntityAccess = firstAccess.entity_access_list[0]

      let role: Role | null = null
      if (firstAccess.root_user.is_superadmin) {
        role = 'superadmin'
      } else {
        role = firstEntityAccess.relations[0].role
      }

      this.changeSession({
        role: roleToSessionRole(role),
        entity_id: firstEntityAccess.entity.id,
      })
      return true
    },

    setAutoSession(tryLastSession = true): boolean {
      const attempt = (strategy: () => boolean): boolean => {
        try {
          return strategy()
        } catch (error) {
          Sentry.captureException(error)
          return false
        }
      }

      if (attempt(() => this.setSessionFromUrl())) {
        return true
      }

      if (tryLastSession && attempt(() => this.setSavedSession())) {
        return true
      }

      if (attempt(() => this.setDefaultSession())) {
        return true
      }

      if (attempt(() => this.setSingleAccountSession())) {
        return true
      }

      return false
    },

    findRoleByEntityId(entity_id: string): SessionRole | null {
      for (const rad of this.access_data) {
        for (const ead of rad.entity_access_list) {
          if (ead.entity.id === entity_id) {
            const role = rad.root_user.is_superadmin
              ? 'superadmin'
              : ead.relations[0].role
            return role ? roleToSessionRole(role) : null
          }
        }
      }
      return null
    },

    handleAssistantClientSelection(mapping: AssistantClientFlatItem) {
      this.activeAssistantClient = mapping
      apiService.setRootUserHeader(mapping.root_user.id)
      UserService.setLastAssistantClient(mapping.relation_id)
    },

    async expireSessions() {
      // endSession=false: session already expired server-side, no need for backend logout call
      await UserService.logout('?expired=true', true, false)
    },

    async registerUser({
      email,
      password,
      first_name,
      last_name,
    }: {
      email: string
      password: string
      first_name: string
      last_name: string
    }) {
      const lowerCaseEmail = email.toLowerCase()
      const hashedPassword = UserService.hashPassword(password, lowerCaseEmail)

      const data = {
        email: lowerCaseEmail,
        password: hashedPassword,
        first_name: first_name,
        last_name: last_name,
      }

      try {
        const response = await UserService.registerUser(data)
        return response
      } catch (err) {
        throw new HttpError(err)
      }
    },

    async getAccessData() {
      this.access_data_loading = true
      try {
        const response = await RootService.getAccessData()

        this.$patch({
          access_data: response.data,
          access_data_loading: false,
        })
      } catch (err) {
        throw new HttpError(err)
      }
    },

    async getMe() {
      try {
        const response = await UserService.getMe()
        this.user = response.data

        Sentry.getCurrentScope().setUser({ email: response.data.email })
      } catch (err) {
        throw new HttpError(err)
      }
    },

    async getUserSettings() {
      try {
        const response = await UserSettingsService.getUserSettings()
        this.setUserSettings(response.data)
      } catch (err) {
        throw new HttpError(err)
      }
    },

    setUserSettings(newSettings: Partial<UserSettings>) {
      const additional = this.settings.additional
      if (newSettings.additional) {
        // accept only valid themes values
        if (
          'theme' in newSettings.additional &&
          newSettings.additional.theme &&
          !themeValues.includes(newSettings.additional.theme)
        ) {
          newSettings.additional.theme = themeValues[0]
        }

        // remove null values except explicitly nullable keys to preserve defaults
        const nullableKeys = [
          'tour_flow',
          'default_session',
          'fund_benchmarks',
          'portfolio_default_filters',
          'pnl_attribution_defaults',
        ]
        Object.keys(newSettings.additional).forEach((key) => {
          if (
            nullableKeys.indexOf(key) == -1 &&
            newSettings.additional![key] == null
          ) {
            delete newSettings.additional![key]
          }
        })
        Object.assign(additional, newSettings.additional)
      }

      this.settings = {
        ...this.settings,
        ...newSettings,
        additional,
      }
    },

    async saveUserSettings(newSettings: Partial<UserSettings>) {
      const { settings } = this

      this.setUserSettings(newSettings)

      try {
        await UserSettingsService.updateUserSettings(newSettings)
      } catch (err) {
        this.setUserSettings(settings)
        throw new HttpError(err)
      }
    },

    async saveUserAdditionalSettings(newSettings: UserSettings['additional']) {
      return this.saveUserSettings({
        additional: {
          ...this.settings.additional,
          ...newSettings,
        },
      })
    },

    async changeCurrency(type: 'fiat' | 'crypto', currencyId: string) {
      const key = type === 'fiat' ? 'base_currency_id' : 'base_crypto_id'

      return this.saveUserSettings({
        [key]: currencyId,
      })
    },

    async changePassword(
      email: string,
      old_password: string,
      new_password: string
    ) {
      const old_hashed = UserService.hashPassword(old_password, email)
      const new_hashed = UserService.hashPassword(
        new_password,
        email.toLowerCase()
      )

      try {
        await UserService.changePassword(old_hashed, new_hashed)
      } catch (error) {
        throw new HttpError(error)
      }
    },

    async updateUserProfile(data: UpdateProfileParams) {
      if (!this.user) {
        return
      }

      const payload: UpdateProfileData = {
        first_name: data.first_name,
        last_name: data.last_name,
      }
      const email = data.email.toLowerCase()
      const currentEmail = this.user.email.toLowerCase()

      if (email !== currentEmail && data.password) {
        payload.email = email
        payload.old_password = UserService.hashPassword(
          data.password,
          currentEmail
        )
        payload.new_password = UserService.hashPassword(data.password, email)
      }

      try {
        const profile = await UserService.updateProfile(payload)
        this.user = {
          ...this.user,
          ...profile,
        }
      } catch (error) {
        throw new HttpError(error)
      }
    },

    async saveTourFlow(tourFlow: TourFlow) {
      return this.saveUserAdditionalSettings({
        tour_flow: tourFlow,
      })
    },

    async saveFundBenchmarks(fundBenchmarks: Record<string, string | null>) {
      return this.saveUserAdditionalSettings({
        fund_benchmarks: fundBenchmarks,
      })
    },

    async changeUserLoginEmail(user_id: string, new_email: string) {
      try {
        await UserService.changeUserLoginEmail(user_id, new_email)
      } catch (error) {
        throw new HttpError(error)
      }
    },

    async getDefaultCurrencies() {
      const baseCurrencyId = this.entity?.fund_product?.base_currency_id

      if (!baseCurrencyId) {
        return
      }

      const globalStore = useGlobalStore()
      const baseCryptoId = globalStore.currencyBySymbol('BTC')?.id

      if (
        this.settings.base_currency_id != baseCurrencyId ||
        this.settings.base_crypto_id != baseCryptoId
      ) {
        return this.saveUserSettings({
          base_currency_id: baseCurrencyId,
          base_crypto_id: baseCryptoId,
        })
      }
    },

    addFeeType(feeType: FeeType) {
      this.fee_types.push(feeType)
    },

    updateFeeType(feeTypeId: string, data: FeeType) {
      const idx = this.fee_types.findIndex(
        (feeType) => feeType.id === feeTypeId
      )
      if (idx !== -1) {
        const feeType = this.fee_types[idx]
        Object.keys(data).forEach((key) => {
          Object.assign(feeType, { [key]: data[key] || feeType[key] })
        })
      }
    },

    removeFeeType(feeTypeId: string) {
      const idx = this.fee_types.findIndex(
        (feeType) => feeType.id === feeTypeId
      )
      if (idx !== -1) {
        this.fee_types.splice(idx, 1)
      }
    },
  },

  getters: {
    entityOrFail(state): Entity {
      if (!state.entity) {
        throw new Error('No entity selected')
      }
      return state.entity
    },

    getEntitiesByRole(state) {
      return (role: SessionRole): Entity[] => {
        const entities: Entity[] = []

        for (const rad of state.access_data) {
          for (const ead of rad.entity_access_list) {
            const hasRole = ead.relations.some((r) => r.role === role)
            if (hasRole && ead.entity.type === 'fund_product') {
              entities.push(ead.entity)
            }
          }
        }

        return entities
      }
    },

    getEntitiesWithDetailsByRole(state) {
      return (role: SessionRole): EntityWithDetails[] => {
        const result: EntityWithDetails[] = []

        for (const rad of state.access_data) {
          for (const ead of rad.entity_access_list) {
            const roleRelation = ead.relations.find((r) => r.role === role)
            if (roleRelation && ead.entity.type === 'fund_product') {
              result.push({
                entity: ead.entity,
                rootUser: rad.root_user,
                sections: roleRelation.client_view_sections ?? [],
              })
            }
          }
        }

        return result
      }
    },

    findRootIdByEntityId(state) {
      return (entityId: string): string | null => {
        for (const rad of state.access_data) {
          if (
            rad.entity_access_list.some((ead) => ead.entity.id === entityId)
          ) {
            return rad.root.id
          }
        }
        return null
      }
    },

    getEntityValuationDates(state) {
      return (entityId: string): ValuationDates | null => {
        for (const rad of state.access_data) {
          for (const ead of rad.entity_access_list) {
            if (ead.entity.id === entityId) {
              return ead.valuation_dates ?? null
            }
          }
        }
        return null
      }
    },

    hasOnlyRole(state) {
      return (role: SessionRole): boolean => {
        const otherRoles = ['admin', 'client', 'referral', 'assistant'].filter(
          (r) => r !== role
        )

        for (const rad of state.access_data) {
          // Superadmin is considered as admin
          if (rad.root_user.is_superadmin && role !== 'admin') {
            return false
          }

          for (const ead of rad.entity_access_list) {
            const hasOtherRole = ead.relations.some((r) =>
              otherRoles.includes(r.role)
            )
            if (hasOtherRole) {
              return false
            }
          }
        }
        return true
      }
    },

    rootOrFail(state): Root {
      if (!state.root) {
        throw new Error('No root selected')
      }
      return state.root
    },

    rootIdOrFail(): string {
      return this.rootOrFail.id
    },

    rootUserOrFail(state): BaseRootUser {
      if (!state.root_user) {
        throw new Error('No root user selected')
      }
      return state.root_user
    },

    rootUserIdOrFail(): string {
      return this.rootUserOrFail.id
    },

    userOrFail(state): CurrentUser {
      if (!state.user) {
        throw new Error('No user data available')
      }
      return state.user
    },

    userIdOrFail(): string {
      return this.userOrFail.id
    },

    entities(state): Entity[] {
      return state.access_data.flatMap((rad) =>
        rad.entity_access_list.map((e) => e.entity)
      )
    },

    allClientEntities(): Entity[] {
      return this.getEntitiesByRole('client')
    },

    allClientEntitiesWithDetails(): EntityWithDetails[] {
      return this.getEntitiesWithDetailsByRole('client')
    },

    allAdminEntities(state): Entity[] {
      const entities: Entity[] = []
      for (const rad of state.access_data) {
        if (rad.root_user.is_superadmin) {
          // Superadmin has access to all entities in their root
          for (const ead of rad.entity_access_list) {
            if (!entities.some((e) => e.id === ead.entity.id)) {
              entities.push(ead.entity)
            }
          }
        } else {
          // Regular admin - check relations
          for (const ead of rad.entity_access_list) {
            const hasAdminRole = ead.relations.some((r) => r.role === 'admin')
            if (hasAdminRole && !entities.some((e) => e.id === ead.entity.id)) {
              entities.push(ead.entity)
            }
          }
        }
      }
      return entities
    },

    hasAnyClientRole(): boolean {
      return this.allClientEntities.length > 0
    },

    hasOnlyClientRole(): boolean {
      return this.hasOnlyRole('client')
    },

    allReferrerEntities(): Entity[] {
      return this.getEntitiesByRole('referral')
    },

    allReferrerEntitiesWithRootUser(): {
      entity: Entity
      rootUser: BaseRootUser
    }[] {
      return this.getEntitiesWithDetailsByRole('referral').map((item) => ({
        entity: item.entity,
        rootUser: item.rootUser,
      }))
    },

    hasAnyReferralRole(): boolean {
      return this.allReferrerEntities.length > 0
    },

    hasOnlyReferralRole(): boolean {
      return this.hasOnlyRole('referral')
    },

    hasOnlyAssistantRole(): boolean {
      return this.hasOnlyRole('assistant')
    },

    hasAssistantRelation(state): boolean {
      return state.access_data.some((rad) =>
        rad.entity_access_list.some((ead) =>
          ead.relations.some((r) => r.role === 'assistant')
        )
      )
    },

    assistantClientsWithEntity(state): AssistantClientFlatItem[] {
      return state.access_data.flatMap((rad) =>
        rad.entity_access_list.flatMap((ead) =>
          ead.relations
            .filter(
              (
                rel
              ): rel is RootUserAccessDataRelation & {
                assistant_clients: AssistantClientMapping[]
              } => rel.role === 'assistant' && !!rel.assistant_clients
            )
            .flatMap((rel) =>
              rel.assistant_clients.map((client) => ({
                ...client,
                entity_name: ead.entity.name,
                fund_entity_id: ead.entity.id,
              }))
            )
        )
      )
    },

    productEntities(): Entity[] {
      return this.entities.filter(
        (entity) => entity && entity.type != 'root_user'
      )
    },

    entitiesGrouped(): EntitiesGroup[] {
      if (!this.root) return []

      // Only offer entities of the currently selected root
      const currentRootId = this.root.id
      const rootEntities = this.productEntities.filter(
        (entity) => !currentRootId || entity.root_id === currentRootId
      )

      const grouped: GroupedEntities = rootEntities.reduce(
        (acc: GroupedEntities, entity: Entity) => {
          if (!(entity.type in acc)) {
            acc[entity.type] = {
              icon: 'Box',
              text: friendlyEntityTypes[entity.type],
              entities: [],
            }
          }

          acc[entity.type]?.entities.push(entity)
          return acc
        },
        {}
      )

      return Object.values(grouped).sort((a, b) => {
        return a.text.localeCompare(b.text)
      })
    },

    entityId(state): string | null {
      return state.entity?.id || null
    },

    entityTimezone(state): Timezone | null {
      if (!state.entity) {
        return null
      }
      return this.getEntityTimezone(state.entity)
    },

    fundProductId(state): string | null {
      return state.entity?.fund_product?.id || null
    },

    entityById() {
      return (entity_id: string): Entity | null => {
        return this.entities.find((entity) => entity.id === entity_id) || null
      }
    },

    rootById() {
      return (root_id: string): Root | null => {
        const rad = this.access_data.find((rad) => rad.root.id === root_id)
        return rad ? rad.root : null
      }
    },

    isFundProduct(state): boolean {
      const { entity } = state
      return entity != null && entity.type == 'fund_product'
    },

    currencyFiat(state): Currency | null {
      if (!state.settings.base_currency_id) {
        return null
      }

      const globalStore = useGlobalStore()
      return globalStore.currencyById(state.settings.base_currency_id)
    },

    currencyCrypto(state): Currency | null {
      if (!state.settings.base_crypto_id) {
        return null
      }

      const globalStore = useGlobalStore()
      return globalStore.currencyById(state.settings.base_crypto_id)
    },

    currencyFiatSymbol(): string {
      return this.currencyFiat?.symbol || '-'
    },

    currencyCryptoSymbol(): string {
      return this.currencyCrypto?.symbol || '-'
    },

    currencyHash(): string | null {
      const currencyFiat = this.currencyFiat
      const currencyCrypto = this.currencyCrypto
      if (!currencyCrypto || !currencyFiat) {
        return null
      }
      return `${currencyCrypto.id}||${currencyFiat.id}`
    },

    currentFundProductId(state): string | null {
      const entity = state.entity

      if (entity && 'fund_product' in entity && entity.fund_product) {
        return entity.fund_product.id
      }

      return null
    },

    sessionTimeout(state): TimeoutOption {
      const globalStore = useGlobalStore()
      const defaultTimeout = globalStore.sessionTimeoutOptions[0]

      if (state.settings.session_timeout) {
        return (
          globalStore.sessionTimeoutOptions.find((option) => {
            return option.value == state.settings.session_timeout
          }) || defaultTimeout
        )
      }
      return defaultTimeout
    },

    tablePagination(state): number {
      return state.settings.additional.table_pagination || 25
    },

    accountName(state): string {
      const { user } = state
      if (user?.first_name) {
        return user.first_name
      } else if (user?.email) {
        return user.email.split('@')[0]
      }
      return ''
    },

    userName(state): string {
      const { user } = state
      if (!user) {
        return ''
      }
      return `${user.last_name} ${user.first_name}`
    },

    dateTimeFormat(state): string {
      return `${state.settings.additional.date_format} HH:mm:ss`
    },

    fundCurrency(state): Currency | null {
      const entity = state.entity

      if (!entity || !entity.fund_product) {
        return null
      }

      const globalStore = useGlobalStore()

      const base_currency_id = entity.fund_product.base_currency_id
      return globalStore.currencyById(base_currency_id) || null
    },

    fundCurrencySymbol(): string {
      return this.fundCurrency?.symbol || '-'
    },

    hasProduct(): boolean {
      return this.productEntities.length > 0
    },

    hasPermission(state) {
      return (permissionId: number): boolean => {
        // Superadmin can access everything, expect hidden permissions
        if (
          state.role === 'superadmin' &&
          !HIDDEN_PERMISSION_IDS.includes(permissionId)
        ) {
          return true
        }

        return state.permissions.indexOf(permissionId) !== -1
      }
    },

    hasPackage(state) {
      return (packageId: string[] | string | number[] | number): boolean => {
        if (Array.isArray(packageId)) {
          const hasSinglePackage = (element: string | number): boolean => {
            return state.packages.indexOf(Number(element)) != -1
          }
          return packageId.some(hasSinglePackage)
        } else {
          return state.packages.indexOf(Number(packageId)) != -1
        }
      }
    },

    hasMultipleAccounts(state): boolean {
      if (state.access_data.length > 1) {
        return true
      }

      return state.access_data.some((root_access) => {
        if (root_access.entity_access_list.length > 1) {
          return true
        }
        return root_access.entity_access_list.some(
          (entity) => entity.relations.length > 1
        )
      })
    },

    hasRelation(state) {
      return (role: SessionRole): boolean => {
        return state.access_data.some((rad) => {
          return rad.entity_access_list.some((entity) => {
            if (role === 'admin' && rad.root_user.is_superadmin) {
              return true
            }
            return entity.relations.some((r) => r.role === role)
          })
        })
      }
    },

    noPermissionsAdmin(state): boolean {
      const isAdmin = state.role == 'admin'
      const hasPermissions = Object.keys(state.permissions).length > 0
      return isAdmin && !hasPermissions
    },

    noPackages(state): boolean {
      return state.packages.length == 0
    },

    homeRoute(state): string {
      const currentView = state.currentView
      let routeName = 'DashboardView'
      switch (currentView) {
        case 'client':
          routeName = 'ClientFundView'
          break
        case 'referral':
          routeName = 'ReferrerView'
          break
        case 'assistant':
          routeName = 'AssistantFundView'
          break
        // no default
      }
      return routeName
    },

    getEntityTimezone() {
      return (entity: Entity): Timezone | null => {
        const timezoneId =
          entity[entity.type] && entity[entity.type]?.timezone_id

        if (timezoneId) {
          const globalStore = useGlobalStore()
          return globalStore.timezoneById(timezoneId)
        }
        return null
      }
    },

    dateToEntityTzString() {
      return (date: Date, entity_id: string): string => {
        // Converts date into a date ISO string with
        // offset set by the entity timezone
        const entity = this.entityById(entity_id)
        let timezone: Timezone | null = null
        let datetime = dateToString(date)

        if (entity) {
          timezone = this.getEntityTimezone(entity)
        }

        if (date && timezone) {
          const offset = militaryGMTOffsetFromNumeric(timezone.time_offset)
          datetime = format(date, "yyyy-MM-dd'T'HH:mm:ss") + offset
        }

        return datetime
      }
    },

    dateToUTCString() {
      return (date: Date, entity_id: string): string => {
        // Converts Date object into a date ISO string
        // with offset 00:00
        const entity = this.entityById(entity_id)
        let timezone: Timezone | null = null

        if (entity) {
          timezone = this.getEntityTimezone(entity)
        }

        if (timezone) {
          const minutes = timezone.time_offset * 60
          date = subMinutes(date, minutes)
          return format(date, "yyyy-MM-dd'T'HH:mm:ss'+00:00'")
        }

        return dateToUTCString(date)
      }
    },

    stringToEntityTzDate(state) {
      return (string: string, entity_id?: string | null): Date => {
        // Converts ISO string into a Date() object in regard to selected entity
        const entity = entity_id ? this.entityById(entity_id) : state.entity
        let timezone: Timezone | null = null
        let date = stringToUTCDate(string)

        if (entity) {
          timezone = this.getEntityTimezone(entity)
        }

        if (date && timezone) {
          // Adjust time
          const minutes = timezone.time_offset * 60
          date = addMinutes(date, date.getTimezoneOffset())
          date = addMinutes(date, minutes)
        }

        return date
      }
    },

    dateWithTzOffset() {
      return (date: Date, entity_id: string): Date => {
        const entity = this.entityById(entity_id)
        let timezone: Timezone | null = null

        if (entity) {
          timezone = this.getEntityTimezone(entity)
        }

        if (timezone) {
          // Adjust time
          const minutes = timezone.time_offset * 60
          date = addMinutes(date, date.getTimezoneOffset())
          date = addMinutes(date, minutes)
        }

        return date
      }
    },

    getEntityDate() {
      return (date: Date): Date => {
        const entityId = this.entityId
        if (!entityId) {
          return date
        }
        return this.dateWithTzOffset(date, entityId)
      }
    },

    utcLastClose() {
      return (entity: Entity): string => {
        return this.utcCloseFromDate(new Date(), entity, true)
      }
    },

    utcCloseFromDate() {
      return (date: Date, entity: Entity | null, notFuture = false): string => {
        const timezone = entity ? this.getEntityTimezone(entity) : null

        const globalStore = useGlobalStore()
        return globalStore.utcCloseByTimezone(
          date,
          timezone?.id ?? null,
          notFuture
        )
      }
    },

    utcOpenFromDate() {
      return (date: Date, entity: Entity | null): string => {
        let timezoneId: string | null = null

        if (entity) {
          timezoneId =
            (entity[entity.type] && entity[entity.type]?.timezone_id) || null
        }

        const globalStore = useGlobalStore()
        return globalStore.utcOpenByTimezone(date, timezoneId)
      }
    },

    formatDateStringInTimeZone(state) {
      return (dateString: string | null, format?: string): string => {
        const entityId = this.entityId
        if (!dateString || !entityId) {
          return '-'
        }

        if (!format) {
          format = state.settings.additional.date_format
        }

        const date = this.stringToEntityTzDate(dateString, entityId)
        return date ? formatDateHelper(date, format) : '-'
      }
    },

    activeTourFlow(state): TourFlow | null {
      const flow = state.settings.additional.tour_flow
      if (flow === null) {
        const isTracker =
          (this.hasPackage('2') || this.hasPackage('37')) &&
          !(this.hasPackage('3') || this.hasPackage('43'))
        const portfolio = this.productEntities.find(
          (entity: Entity) => entity.type == 'portfolio'
        )

        if (isTracker && !portfolio) {
          return {
            scenario_key: 'onboarding',
            current_step: 0,
            finished: false,
          }
        }
      }

      return flow?.finished ? null : flow
    },

    weekStartForApi(state): Day {
      return ((state.settings.additional.week_starts_on + 6) % 7) as Day
    },

    customFeeTypes(state) {
      return state.fee_types
        .filter((type) => type.id != 'transaction')
        .sort((a, b) => {
          return a.name.localeCompare(b.name)
        })
    },

    feeTypeById(state) {
      return (id: string): FeeType | null => {
        return state.fee_types.find((type) => type.id === id) || null
      }
    },

    feeTypesForEntity(state) {
      return (entityId: string): FeeType[] => {
        for (const rad of state.access_data) {
          const ead = rad.entity_access_list.find(
            (e) => e.entity.id === entityId
          )
          if (ead) {
            return ead.fee_types
          }
        }
        return []
      }
    },

    getBenchmarkForFund(state) {
      return (fundId: string): string | null => {
        const fundBenchmarks = state.settings.additional.fund_benchmarks
        if (fundBenchmarks && fundId in fundBenchmarks) {
          return fundBenchmarks[fundId]
        }
        // No fallback - return null if not set for this fund
        return null
      }
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot))
}
